import React from 'react';
import {
  LuActivity,
  LuChartBar,
  LuChartCandlestick,
  LuCalendarClock,
  LuCloudSun,
  LuShieldCheck,
  LuMegaphone,
  LuSpline,
  LuCalendarCheck,
  LuChartNoAxesColumn,
  LuGauge,
  LuGitCompare,
  LuGrid2X2,
  LuGrid3X3,
  LuHistory,
  LuLayers,
  LuLayoutDashboard,
  LuLayoutGrid,
  LuLayoutTemplate,
  LuListChecks,
  LuNewspaper,
  LuPercent,
  LuRadar,
  LuScanLine,
  LuShuffle,
  LuSigma,
  LuSlidersHorizontal,
  LuStar,
  LuTable2,
  LuTarget,
  LuWaves,
  LuZap,
  type IconComponent,
} from './icons';

/**
 * Tool id → line icon.
 *
 * App.tsx still carries a `icon: string` glyph on every nav entry, and other
 * callers may rely on that shape, so the nav contract is untouched: this map is
 * consulted first and the glyph is the fallback for any id it does not know.
 * That means a new tool added to App.tsx renders immediately with its glyph
 * and picks up a line icon the moment a row is added here.
 */
const TOOL_ICONS: Record<string, IconComponent> = {
  // Overview
  'data-table': LuTable2,
  'elite-pulse': LuRadar,
  'dashboard-mode': LuLayoutDashboard,
  'analytics-hub': LuGauge,
  'tracked-contracts': LuStar,

  // Flow
  'options-flow': LuWaves,
  'flow-heatmap': LuGrid3X3,
  'flow-matrix': LuGrid2X2,

  // Volatility
  'iv-tracking': LuPercent,

  // Protect
  protect: LuShieldCheck,

  // Edge Journey
  'edge-scanner': LuScanLine,
  'market-climate': LuGauge,
  'path-cloud': LuCloudSun,
  'structure-lab': LuSpline,
  'daily-edge': LuCalendarCheck,

  // News
  'news-feed': LuNewspaper,
  'event-outlook': LuCalendarClock,
  'statement-tracker': LuMegaphone,
  'net-drift': LuWaves,

  // GEX
  'volume-oi-spotgamma': LuChartBar,
  'vertical-spotgamma-oi': LuChartNoAxesColumn,
  'gamma-shift': LuShuffle,
  'realtime-gex': LuActivity,

  // Charts
  'detailed-options': LuChartCandlestick,

  // Tools
  'sigma-range-calc': LuSigma,
  'option-change-tracker': LuGitCompare,
  'trading-calls': LuTarget,
  'trading-call-tracker': LuListChecks,
  'trading-calls-widget': LuLayoutTemplate,
  'trading-call-generator': LuZap,

  // Legacy RTL
  'volume-delta-reformed': LuLayers,
  'trades-chart-reformed': LuChartCandlestick,
};

const GROUP_ICONS: Record<string, IconComponent> = {
  'protect-group': LuShieldCheck,
  overview: LuLayoutGrid,
  flow: LuWaves,
  volatility: LuPercent,
  edge: LuScanLine,
  news: LuNewspaper,
  gex: LuActivity,
  charts: LuChartCandlestick,
  tools: LuSlidersHorizontal,
  'legacy-rtl-volume': LuHistory,
};

interface NavIconProps {
  /** Tool or group id. */
  id: string;
  /** The glyph App.tsx supplied; used when no line icon is registered. */
  fallback?: string;
  kind?: 'tool' | 'group';
  size?: number;
  className?: string;
}

export const NavIcon: React.FC<NavIconProps> = ({
  id,
  fallback,
  kind = 'tool',
  size = 16,
  className,
}) => {
  const Icon = (kind === 'group' ? GROUP_ICONS : TOOL_ICONS)[id];
  if (Icon) {
    return <Icon size={size} strokeWidth={1.75} aria-hidden className={className} />;
  }
  return (
    <span
      aria-hidden
      className={className}
      style={{ fontSize: size * 0.85, lineHeight: 1, display: 'inline-block', width: size, textAlign: 'center' }}
    >
      {fallback ?? '•'}
    </span>
  );
};

export type { IconComponent };

export function hasToolIcon(id: string): boolean {
  return Boolean(TOOL_ICONS[id]);
}
